import { CommonModule } from "@angular/common";
import { Component, inject, signal, OnInit, output } from "@angular/core";
import { AudioExercise } from "@domain/Exercise";
import { Result } from "@domain/Result";
import { SequenceService } from "@services/sequence.service";

@Component({
  selector: "app-sequence-results",
  imports: [CommonModule],
  template: `
    @let results = sequenceService.sequence().getResults();

    <div class="results tab-content">
      <h2 class="results__title">Série d’exercices terminée!</h2>
      <p class="results__number-of-correct-answers body-3" data-testid="number-of-correct-answer-message">
        {{ getNumberOfCorrectAnswersMessage() }}
      </p>
      <ul class="results__list card-content">
        @for (result of results; track result.exercise.id.toString()) {
          @let exerciseId = result.exercise.id.toString();
          @let numberOfTries = result.numberOfTries;
          @let isResultCorrect = result.answer.isCorrect();
          @let resultCorrectAnswer = result.exercise.correctAnswer;

          <li [attr.data-testid]="'result-' + exerciseId" class="results__list-item">
            @if (isResultCorrect) {
              <img src="assets/icons/correct-answer.svg" alt="Correct answer icon" />
            } @else {
              <img src="assets/icons/incorrect-answer.svg" alt="Incorrect answer icon" />
            }

            <div class="result__content">
              <div class="result__sentence">
                <p class="sentence__content body-2" data-testid="result-sentence" [innerHTML]="getResultSentence(result)"></p>
                @if (numberOfTries > 1) {
                  <span class="result__number-of-try result__number-of-try--desktop"
                    ><span class="number-of-try__content body-3" data-testid="number-of-tries-desktop">Essais : {{ numberOfTries }}</span></span
                  >
                }
              </div>

              @if (isResultCorrect) {
                <p class="subtitle-3" data-testid="result-correct-answer">
                  La réponse était bien :
                  <span class="result__correct-answer">{{ resultCorrectAnswer }}</span>
                </p>
              } @else {
                <p class="subtitle-3" data-testid="result-correct-answer">
                  La réponse était :
                  <span class="result__correct-answer">{{ resultCorrectAnswer }}</span>
                </p>
              }

              @if (numberOfTries > 1) {
                <span class="result__number-of-try result__number-of-try--mobile"
                  ><span class="number-of-try__content body-3" data-testid="number-of-tries-mobile">Essais : {{ numberOfTries }}</span></span
                >
              }
            </div>
          </li>
        }
      </ul>
    </div>
  `,
  styles: `
    @use "responsive" as *;
    @use "typography" as *;

    .results {
      width: 100%;
      height: calc(100vh - 350px);
      display: flex;
      flex-direction: column;
    }

    .results__title {
      flex-shrink: 0;
      flex-grow: 0;
      text-align: center;
      margin-bottom: 4px;
      @include titleH4();

      @include screenWidth("from-desktop") {
        @include titleH5();
      }
    }

    .results__number-of-correct-answers {
      flex-shrink: 0;
      flex-grow: 0;
      text-align: center;
      margin-bottom: 20px;
    }

    .results__list {
      flex-shrink: 1;
      flex-grow: 1;

      /* WebKit-based browsers (Chrome, Edge, Safari, etc.) */
      &::-webkit-scrollbar {
        width: 8px;
      }

      &::-webkit-scrollbar-track {
        background: transparent;
      }

      &::-webkit-scrollbar-thumb {
        background: #e4e0d8;
        border-radius: 100px;
      }

      &::-webkit-scrollbar-thumb:hover {
        background: #d1cdc5;
      }

      /* Firefox */
      scrollbar-width: thin;
      scrollbar-color: #e4e0d8 transparent;

      @include screenWidth("to-tablet") {
        padding-left: 16px;
        padding-right: 16px;
      }
    }

    .results__list-item {
      display: flex;
      align-items: flex-start;
      gap: 8px;

      &:not(:last-child) {
        margin-bottom: 24px;
      }
    }

    .result__content {
      display: flex;
      flex-direction: column;
      align-items: flex-start;
      gap: 8px;
    }

    .result__sentence {
      display: flex;
      align-items: baseline;
      flex-wrap: no-wrap;
      justify-content: flex-start;
      gap: 8px;
    }

    .sentence__content {
      flex-shrink: 1;
      text-wrap: pretty;
      hyphens: auto;
      word-break: break-word;
    }

    .result__number-of-try {
      flex-shrink: 0;
      float: right;
      display: inline-flex;
      padding: 4px 12px;
      justify-content: center;
      align-items: center;
      gap: 10px;
      border-radius: 20px;
      border: 1px solid #05bf8e;
      background: #cdf2e8;

      &.result__number-of-try--mobile {
        @include notOnDesktop();
      }

      &.result__number-of-try--desktop {
        @include onDesktopOnly();
      }
    }

    .number-of-try__content {
      color: #03684e;
    }

    .result__correct-answer {
      color: #05bf8e;
      order: 0;
      flex-basis: 100%;
    }
  `,
})
export class SequenceResults implements OnInit {
  protected sequenceService = inject(SequenceService);

  protected startNewSequence = output<void>();
  protected quitExerciser = output<void>();

  protected numberOfCorrectAnswers = signal<number>(0);

  ngOnInit() {
    this.numberOfCorrectAnswers.set(this.sequenceService.sequence().getCorrectResults().length);
  }

  getResultSentence(result: Result) {
    if (result.exercise instanceof AudioExercise) {
      return result.exercise.sentence.replace("[textfield]", `<strong class="underlined regular">${result.answer.value}</strong>`);
    }

    return result.exercise.sentence.replace("[dropdown]", `<strong class="underlined regular">${result.answer.value}</strong>`);
  }

  getNumberOfCorrectAnswersMessage() {
    if (this.numberOfCorrectAnswers() === 0) {
      return "Tu n'as aucune bonne réponse, refais l'exercice afin de t'améliorer !";
    }

    if (this.numberOfCorrectAnswers() === 1) {
      return "Tu as 1 seule bonne réponse, refais l'exercice afin de t'améliorer !";
    }

    return `Tu as ${this.numberOfCorrectAnswers()} bonnes réponses, continue comme ça !`;
  }
}
